import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ago } from "./AdminNotifications";

const STATUS_LABEL = { new: "New", read: "Read", done: "Done" };
const GUEST_MODES = [
  { key: "open", label: "Anyone can send", note: "Guests can send a suggestion without an account." },
  { key: "ask", label: "Ask guests to log in after their first message", note: "A guest's first message still reaches you, and they are asked to create a free login. More messages from that guest are not delivered until they log in. This is how it starts." },
  { key: "strict", label: "Guests must log in first (failsafe)", note: "Nothing from a guest is delivered. Use this if robots start sending junk." },
];
const KIND_LABEL = { cancel: "Canceled", suggestion: "Suggestion", song_request: "Song request" };
const FILTERS = [["all", "All"], ["problem", "Problems"], ["song_request", "Song requests"], ["suggestion", "Suggestions"], ["cancel", "Cancels"]];

// Admin: what members told Dan (cancel reasons and the suggestion box).
export default function AdminFeedback() {
  const [data, setData] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const [kind, setKind] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/feedback").then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);   // the numbers refresh by themselves every minute
    return () => clearInterval(t);
  }, [load]);

  const setGuestMode = async (mode) => {
    const before = data.guest_mode;
    setData((d) => ({ ...d, guest_mode: mode, guests_need_login: mode !== "open" }));
    try { await api.put("/admin/feedback/settings", { guest_mode: mode }); toast.success("Saved"); }
    catch (e) { setData((d) => ({ ...d, guest_mode: before, guests_need_login: before !== "open" })); toast.error(e.response?.data?.detail || "Could not save that"); }
  };

  const mark = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      setData((d) => {
        const items = d.items.map((x) => (x.id === id ? { ...x, status } : x));
        return { ...d, items, open_count: items.filter((x) => x.status === "new").length };
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update that one"); }
  };

  const freeWeek = async (x) => {
    const who = x.name || x.email || "this member";
    if (!confirm(`Give ${who} a free week of Premium, and email them to say so? It adds a week each time you press it.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${x.user_id}/free-week`);
      if (data.free_week_email_sent) toast.success(`${who} has a free week, and the email was sent`);
      else toast.warning(`${who} has a free week, but the email could not be sent. Let them know yourself.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not give the free week"); }
  };

  if (failed) {
    return (
      <div data-testid="admin-feedback">
        <h2 className="font-display text-2xl font-bold mb-4">Feedback</h2>
        <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6 text-sm text-zinc-400">The feedback list could not be loaded. Refresh the page and try again.</div>
      </div>
    );
  }
  if (!data) return null;

  const items = data.items.filter((x) => (showDone || x.status !== "done") && (kind === "all" || x.kind === kind || (kind === "problem" && (x.topic === "problem" || x.reason === "problem"))));

  return (
    <div data-testid="admin-feedback">
      <h2 className="font-display text-2xl font-bold mb-4">Feedback{data.open_count > 0 && <span className="ml-3 align-middle text-xs font-mono-ui px-2 py-1 rounded-full border border-pink-400/60 text-pink-300">{data.open_count} new</span>}</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6">
        <fieldset className="pb-4 mb-4 border-b border-white/10" data-testid="admin-feedback-guestrule">
          <legend className="text-sm font-semibold mb-2">When a guest (not logged in) sends a suggestion</legend>
          <div className="space-y-2" role="radiogroup">
            {GUEST_MODES.map((m) => (
              <label key={m.key} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border cursor-pointer ${data.guest_mode === m.key ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 hover:bg-white/5"}`}>
                <input type="radio" name="guest-mode" checked={data.guest_mode === m.key} onChange={() => setGuestMode(m.key)} data-testid={`admin-feedback-guest-${m.key}`} className="w-4 h-4 mt-1 accent-cyan-400" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{m.label}</span>
                  <span className="block text-xs text-zinc-500">{m.note}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500">Members who canceled Premium, last 30 days</div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="admin-feedback-reasons">
          <div className="rounded-xl border border-cyan-400/40 bg-cyan-400/5 p-3" data-testid="admin-cancel-total">
            <div className="font-display text-3xl font-bold tabular-nums">{data.canceled_30d}</div>
            <div className="text-xs text-zinc-400">Canceled in total</div>
          </div>
          {data.reasons_30d.map((r) => (
            <div key={r.key} className={`rounded-xl border border-white/10 p-3 ${r.count ? "" : "opacity-50"}`} data-testid={`admin-cancel-${r.key}`}>
              <div className="font-display text-3xl font-bold tabular-nums">{r.count}</div>
              <div className="text-xs text-zinc-400">{r.label}</div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-zinc-500">Updates by itself every minute. Counts members who pressed Cancel Premium on their Account page.</p>
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Show">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} data-testid={`admin-feedback-filter-${k}`}
              className={`px-3 py-1.5 rounded-full border text-xs ${kind === k ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{label}</button>
          ))}
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer w-fit">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="w-4 h-4 accent-cyan-400" data-testid="admin-feedback-showdone" /> Show the ones marked Done
        </label>
        <div className="mt-2" data-testid="admin-feedback-list">
          {items.length === 0 && <div className="py-6 text-sm text-zinc-500">Nothing here yet.</div>}
          {items.map((x) => (
            <div key={x.id} data-testid={`admin-feedback-${x.id}`} className="border-t border-white/10 first:border-t-0 py-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {x.status === "new" && <span className="font-mono-ui px-2 py-0.5 rounded-full border border-pink-400/60 text-pink-300">NEW</span>}
                <span className="font-mono-ui px-2 py-0.5 rounded-full border border-white/15 text-zinc-300">{x.kind === "cancel" ? `Canceled${x.reason_label ? ` · ${x.reason_label}` : ""}` : (x.kind === "suggestion" && x.topic_label ? `Suggestion · ${x.topic_label}` : (KIND_LABEL[x.kind] || "Suggestion"))}</span>
                {x.status !== "new" && <span className="text-zinc-500">{STATUS_LABEL[x.status] || x.status}</span>}
              </div>
              {x.style && <div className="mt-2 text-xs text-amber-300">Style or mood: {x.style}</div>}
              {x.message ? <div className="mt-2 text-sm whitespace-pre-wrap break-words">{x.message}</div> : <div className="mt-2 text-sm text-zinc-500">(no message)</div>}
              <div className="mt-1 text-xs text-zinc-500 break-words">
                {[x.name || "", x.plan, x.device ? `${x.device}${x.page ? `, ${x.page}` : ""}` : "", ago(x.created_at)].filter(Boolean).join(" · ")}
              </div>
              {x.reply_to && (
                <div className="mt-1 text-xs text-cyan-300 break-all">Would like a reply: <a href={`mailto:${x.reply_to}`} className="underline">{x.reply_to}</a></div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {x.status === "new" && <button type="button" onClick={() => mark(x.id, "read")} data-testid={`admin-feedback-read-${x.id}`} className="px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/5 text-xs">Mark as read</button>}
                {x.status !== "done" && <button type="button" onClick={() => mark(x.id, "done")} data-testid={`admin-feedback-done-${x.id}`} className="px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/5 text-xs">Done</button>}
                {x.kind === "cancel" && x.user_id && <button type="button" onClick={() => freeWeek(x)} data-testid={`admin-feedback-freeweek-${x.id}`} className="px-3 py-1.5 rounded-full border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 text-xs">Give a free week and email them</button>}
                {x.status === "done" && <button type="button" onClick={() => mark(x.id, "new")} data-testid={`admin-feedback-reopen-${x.id}`} className="px-3 py-1.5 rounded-full border border-white/20 hover:bg-white/5 text-xs">Put back as new</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
